import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BedDouble, Check, ChevronRight, MapPin, Ticket, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Seo, breadcrumbLd, faqLd } from "../lib/seo";
import { EASE, MaskedLines, Reveal, Chapter, ParallaxImg } from "../lib/motion";
import { speLink, trackOutbound } from "../lib/track";
import { SearchWidget } from "../components/SearchWidget";
import { BookingCta } from "../components/BookingCta";
import { GuidePrice } from "../components/GuidePrice";
import { BreakCard } from "../components/BreakCard";

export default function BreakDetail() {
  const { slug } = useParams();
  const [brk, setBrk] = useState(null);
  const [related, setRelated] = useState([]);
  const [missing, setMissing] = useState(false);
  const scrollerRef = useRef(null);

  const scrollExperiences = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-testid^='experience-highlight-']");
    const w = card ? card.getBoundingClientRect().width + 20 : 360;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  useEffect(() => {
    setBrk(null);
    setMissing(false);
    api.get(`/breaks/${slug}`)
      .then((r) => {
        setBrk(r.data);
        api.get("/breaks").then((all) => setRelated(all.data.filter((b) => b.slug !== slug).slice(0, 3)));
      })
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="container-tpb py-24 text-center" data-testid="break-not-found">
        <h1 className="font-serif text-4xl">That break has wandered off</h1>
        <Link to="/breaks" className="btn-primary mt-6" data-testid="back-to-breaks">Browse all breaks</Link>
      </main>
    );
  }
  if (!brk) return <main className="container-tpb py-24" data-testid="break-loading"><p className="text-ink-muted">Loading…</p></main>;

  const spePackage = brk.spe_deep_link?.includes("/packages/") ? brk.spe_deep_link.split("/packages/")[1] : "";
  const detailsUrl = speLink(brk.spe_deep_link, `details-${brk.slug}`);

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Breaks", path: "/breaks" },
      { name: brk.name, path: `/breaks/${brk.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: brk.name,
      description: brk.summary,
      image: brk.hero_image,
      brand: { "@type": "Brand", name: "The Perfect Break" },
      ...(brk.guide_price != null && {
        offers: {
          "@type": "Offer",
          priceCurrency: "GBP",
          price: brk.guide_price,
          priceValidUntil: brk.price_valid_until,
          availability: "https://schema.org/InStock",
          url: `https://theperfectbreak.co.uk/breaks/${brk.slug}`,
        },
      }),
    },
    ...(brk.faqs?.length ? [faqLd(brk.faqs)] : []),
  ];

  return (
    <main data-testid="break-detail-page">
      <Seo
        title={`${brk.name} | ${brk.destination_name} Short Break for Two | The Perfect Break`}
        description={`${brk.tagline} ${brk.nights} night B&B + ${brk.experiences_count} experiences of your choice${brk.guide_price ? `, from £${brk.guide_price} for two` : ""}. ${brk.summary.slice(0, 90)}…`}
        path={`/breaks/${brk.slug}`}
        jsonLd={jsonLd}
      />

      {/* HERO — full-bleed parallax with masked title */}
      <section className="relative overflow-hidden bg-primary" data-testid="break-hero">
        <ParallaxImg src={brk.hero_image} alt={brk.name} className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/30" />
        <div className="container-tpb relative flex min-h-[72vh] flex-col justify-end pb-10 pt-32 sm:min-h-[80vh]">
          <motion.nav
            className="mb-5 flex items-center gap-1.5 text-xs text-sand/90"
            aria-label="Breadcrumb"
            data-testid="breadcrumbs"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          >
            <Link to="/" className="hover:underline">Home</Link> <ChevronRight className="h-3 w-3" />
            <Link to="/breaks" className="hover:underline">Breaks</Link> <ChevronRight className="h-3 w-3" />
            <span aria-current="page">{brk.name}</span>
          </motion.nav>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-sand/85">
            <MapPin className="h-4 w-4" /> {brk.destination_name}
          </p>
          <h1 className="mt-4 max-w-4xl font-serif text-5xl font-medium leading-[1.0] tracking-tight text-sand sm:text-7xl" style={{ textShadow: "0 2px 26px rgba(0,0,0,0.5)" }} data-testid="break-name">
            <MaskedLines immediate delay={0.35} lines={[brk.name]} />
          </h1>
          <motion.p
            className="mt-4 font-serif text-2xl italic text-sand/90"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            data-testid="break-tagline"
          >
            {brk.tagline}
          </motion.p>
        </div>
      </section>

      {/* PRICE + CTA CARD */}
      <section className="container-tpb relative z-10 -mt-1">
        <Reveal>
          <div className="rounded-lg border border-sandline bg-white p-6 shadow-md sm:p-9" data-testid="break-price-card">
            <div className="flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm font-medium text-ink">
                <span className="inline-flex items-center gap-2"><BedDouble className="h-4 w-4 text-primary" /> {brk.nights} night{brk.nights > 1 ? "s" : ""} B&amp;B</span>
                <span className="inline-flex items-center gap-2"><Ticket className="h-4 w-4 text-primary" /> Choose any {brk.experiences_count} experiences — included</span>
              </div>
              <div className="lg:text-right" data-testid="break-price-block">
                <GuidePrice brk={brk} large />
              </div>
            </div>
            <div className="mt-7 border-t border-sandline pt-6">
              <SearchWidget presetPackage={spePackage} placement="break-search" />
              <p className="mt-4 text-sm text-ink-muted">
                Prefer to browse first?{" "}
                <a
                  href={detailsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackOutbound({ break_slug: brk.slug, destination_url: brk.spe_deep_link, placement: "break-details-link" })}
                  className="link-sweep inline-flex items-center gap-1 font-semibold text-primary"
                  data-testid="break-full-details-link"
                >
                  See full package details <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 01 — WHAT'S INCLUDED + EXPERIENCES */}
      <section className="container-tpb py-20 sm:py-28" data-testid="whats-included">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Chapter num="№ 01" overline="What's included" title={["One price.", "All of this."]} />
            <div className="mt-10">
              {brk.inclusions.map((inc, i) => (
                <Reveal key={inc} delay={i * 0.06}>
                  <div className="flex items-start gap-3 border-t border-sandline py-5 text-ink" data-testid="inclusion-item">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Check className="h-3.5 w-3.5 text-primary" />
                    </span>
                    {inc}
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-sandline" />
            </div>
            <Reveal delay={0.2}>
              <div className="mt-10 rounded-lg bg-sand-dark p-7">
                <h2 className="font-sans text-lg font-semibold text-ink">Why we recommend it</h2>
                <p className="mt-2 leading-relaxed text-ink-muted" data-testid="why-recommend">{brk.why_we_recommend}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 02 — EXPERIENCES SCROLLER */}
      <section className="container-tpb pb-20 sm:pb-28" data-testid="break-experiences-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Chapter num="№ 02" overline="Your experiences" title={[`Pick any ${brk.experiences_count} —`, { text: "they're included", className: "italic" }]} />
          <Reveal delay={0.2}>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink-muted" data-testid="break-experiences-count-line">
              Choose any {brk.experiences_count} — {brk.experience_highlights.length} to pick from
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-5 max-w-xl leading-relaxed text-ink-muted">{brk.highlights_note}</p>
        </Reveal>
        <div className="mt-10">
          <div ref={scrollerRef} className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="break-experience-scroller">
            {brk.experience_highlights.map((e, i) => (
              <a
                key={e.name}
                href={speLink(e.spe_url, `highlight-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutbound({ break_slug: brk.slug, destination_url: e.spe_url, placement: "experience-highlight" })}
                className="group w-[72vw] shrink-0 snap-start sm:w-[340px]"
                data-testid={`experience-highlight-${i}`}
              >
                <figure>
                  <div className="aspect-[4/3] overflow-hidden rounded-lg bg-sand-dark">
                    <img src={e.image} alt={e.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
                  </div>
                  <figcaption className="mt-3">
                    <h3 className="font-serif text-xl font-semibold text-ink transition-colors duration-300 group-hover:text-primary">{e.name}</h3>
                    <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-ink-muted">{e.blurb}</p>
                    <p className="link-sweep mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-primary">
                      See the exact offer <ArrowUpRight className="h-3.5 w-3.5" />
                    </p>
                  </figcaption>
                </figure>
              </a>
            ))}
          </div>
          <div className="mt-5 flex items-center gap-3">
            <button type="button" onClick={() => scrollExperiences(-1)} aria-label="Scroll experiences back" className="flex h-11 w-11 items-center justify-center rounded-full border border-sandline text-ink transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-sand" data-testid="scroller-prev-btn">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => scrollExperiences(1)} aria-label="Scroll experiences forward" className="flex h-11 w-11 items-center justify-center rounded-full border border-sandline text-ink transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-sand" data-testid="scroller-next-btn">
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="ml-2 text-xs text-ink-muted sm:hidden" data-testid="experiences-swipe-hint">Swipe for more</p>
          </div>
        </div>
      </section>

      {/* 03 — HOTEL */}
      <section className="border-y border-sandline bg-white/60 py-20 sm:py-28" data-testid="hotel-section">
        <div className="container-tpb grid items-center gap-12 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Chapter num="№ 03" overline="Your hotel" title={["Hand-picked,", "never just handy"]} />
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink-muted" data-testid="hotel-flavour">{brk.hotel_flavour}</p>
              <p className="mt-4 max-w-xl leading-relaxed text-ink-muted">{brk.summary}</p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-2" delay={0.1}>
            <div className="overflow-hidden rounded-lg">
              <img src={brk.card_image} alt={`Hotel on the ${brk.name}`} loading="lazy" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQS */}
      {brk.faqs?.length > 0 && (
        <section className="container-tpb py-20 sm:py-28" data-testid="break-faqs">
          <Chapter num="№ 04" overline="Good to know" title="Questions, answered" />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {brk.faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="h-full rounded-lg border border-sandline bg-white p-7" data-testid={`break-faq-${i}`}>
                  <h2 className="font-sans text-base font-semibold text-ink">{f.q}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-ink-muted">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-primary py-20 sm:py-24" data-testid="break-final-cta">
        <div className="container-tpb flex flex-col items-start gap-8 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="max-w-xl font-serif text-3xl font-medium leading-tight text-sand sm:text-5xl">
            <MaskedLines lines={[`Ready for`, { text: `${brk.destination_name}?`, className: "italic" }]} />
          </h2>
          <Reveal delay={0.2}>
            <div className="rounded-lg bg-white/10 p-7 backdrop-blur-sm">
              <p className="mb-4 text-sm text-sand/80">Live dates, hotel choice and today's price are on the next step.</p>
              <BookingCta brk={brk} placement="break-final" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="container-tpb py-20 sm:py-28" data-testid="related-breaks">
          <Chapter num="№ 05" overline="Keep looking" title="You might also like" />
          <div className="mt-14 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((b, i) => <BreakCard key={b.slug} brk={b} index={i} />)}
          </div>
        </section>
      )}

      {/* STICKY MOBILE CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-sandline bg-white/95 px-4 py-3 backdrop-blur-xl sm:hidden" data-testid="sticky-mobile-cta">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            {brk.guide_price != null ? (
              <p className="whitespace-nowrap font-serif text-lg font-semibold text-primary">From £{brk.guide_price} <span className="font-sans text-xs font-semibold text-ink">{brk.price_basis}</span></p>
            ) : (
              <p className="text-sm font-semibold text-primary">See current price</p>
            )}
            <p className="text-[10px] text-ink-muted">{brk.nights} night · {brk.experiences_count} experiences incl.</p>
          </div>
          <BookingCta brk={brk} placement="sticky-mobile" large={false} />
        </div>
      </div>
      <div className="h-20 sm:hidden" aria-hidden="true" />
    </main>
  );
}
